import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import Img from '@/images';

const propTypes = {
  imgPath: PropTypes.string.isRequired,
  imageUrl: PropTypes.string,
  responsiveImages: PropTypes.shape({
    desktopImageUrl: PropTypes.string,
    mobileImageUrl: PropTypes.string
  }),
  imageTypeFallback: PropTypes.string,
  altImage: PropTypes.string,
  className: PropTypes.string,
  rounded: PropTypes.bool,
  fetchpriority: PropTypes.string,
  loading: PropTypes.string,
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onClick: PropTypes.func
};

const createSrcSet = (imgPath, imageUrl, imageType, version) => `${imgPath}${imageUrl}.${imageType}?v=${version} 1x, ${imgPath}${imageUrl}_2x.${imageType}?v=${version} 2x`;

const PictureWrapper = ({
  imgPath,
  imageUrl = null,
  responsiveImages = {},
  imageTypeFallback = 'jpg',
  altImage = 'Workable graphic',
  className = null,
  rounded = false,
  fetchpriority = null,
  loading = 'lazy',
  width = null,
  height = null,
  onClick
}) => {
  const version = process.env.ASSET_VERSION || 'defaultVersion';

  // Backward compatibility: if imageUrl is provided, use it as desktopImageUrl
  const {desktopImageUrl, mobileImageUrl} = responsiveImages;
  const finalDesktopUrl = desktopImageUrl || imageUrl;
  const imageClassName = cx(className, rounded && 'rounded') || null;

  return (
    <picture onClick={onClick}>
      {finalDesktopUrl && mobileImageUrl ? (
        // Both images provided - use mobile-first breakpoint logic
        <>
          <source
            media="(min-width: 993px)"
            srcSet={createSrcSet(imgPath, finalDesktopUrl, 'webp', version)}
            type="image/webp"
          />
          <source
            media="(min-width: 993px)"
            srcSet={createSrcSet(imgPath, finalDesktopUrl, imageTypeFallback, version)}
            type={`image/${imageTypeFallback}`}
          />
          <source
            media="(min-width: 992px)"
            srcSet={createSrcSet(imgPath, mobileImageUrl, 'webp', version)}
            type="image/webp"
          />
          <source
            media="(min-width: 992px)"
            srcSet={createSrcSet(imgPath, mobileImageUrl, imageTypeFallback, version)}
            type={`image/${imageTypeFallback}`}
          />
        </>
      ) : (
        // Single image - render same image on all breakpoints
        <>
          <source
            srcSet={createSrcSet(imgPath, finalDesktopUrl, 'webp', version)}
            type="image/webp"
          />
          <source
            srcSet={createSrcSet(imgPath, finalDesktopUrl, imageTypeFallback, version)}
            type={`image/${imageTypeFallback}`}
          />
        </>
      )}

      <Img
        srcSet={createSrcSet(
          imgPath,
          mobileImageUrl || finalDesktopUrl,
          imageTypeFallback,
          version
        )}
        src={`${imgPath}${mobileImageUrl || finalDesktopUrl}_2x.${imageTypeFallback}?v=${version}`}
        alt={altImage}
        className={imageClassName}
        {...(fetchpriority ? {fetchpriority} : {})}
        {...(loading ? {loading} : {})}
        {...(width ? {width} : {})}
        {...(height ? {height} : {})}
      />
    </picture>
  );
};

PictureWrapper.propTypes = propTypes;

export default PictureWrapper;
